import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { inAppNotificationsForUserState, userNotificationsBusState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import { formatDateTime, formatRelative } from '@/util/dates';
import type { ChannelOutcome, UserId } from '@rock/types';
import type { FC } from 'react';

export const UserNotificationsTab: FC<{ userId: UserId }> = ({ userId }) => (
    <div className="flex flex-col gap-6">
        <BusCard userId={userId} />
        <InboxCard userId={userId} />
    </div>
);

/** The notification event bus — one row per notification with its per-channel send outcomes. */
const BusCard: FC<{ userId: UserId }> = ({ userId }) => {
    const [events, eventsLoadable] = useLoadableAtomValue(userNotificationsBusState(userId), []);

    return (
        <Card>
            <CardHeader>
                <CardTitle>Notification events</CardTitle>
                <CardDescription>
                    Every notification produced for this user, with what each channel decided (the server-only bus at
                    /user-notifications).
                </CardDescription>
            </CardHeader>
            <CardContent>
                {events.length === 0 ? (
                    <div className="section-description">
                        {eventsLoadable.state === 'loading'
                            ? 'Loading notification events…'
                            : eventsLoadable.state === 'hasError'
                              ? 'Couldn’t load notification events — the admin-read rules/index may not be deployed yet.'
                              : 'No notifications produced yet.'}
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Type</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Push</TableHead>
                                <TableHead>In-app</TableHead>
                                <TableHead className="text-right">Created</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {events.map((event) => (
                                <TableRow key={event.id}>
                                    <TableCell className="font-medium">{event.type}</TableCell>
                                    <TableCell>
                                        <ChannelOutcomeBadge outcome={event.email} />
                                    </TableCell>
                                    <TableCell>
                                        <ChannelOutcomeBadge outcome={event.push} />
                                    </TableCell>
                                    <TableCell>
                                        <ChannelOutcomeBadge outcome={event.app} />
                                    </TableCell>
                                    <TableCell className="text-right text-muted-foreground">
                                        {formatDateTime(event.createdAt)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
};

/** A channel's decision: Sent / a skip reason / an error, or pending when the listener hasn't run. */
const ChannelOutcomeBadge: FC<{ outcome: ChannelOutcome | null | undefined }> = ({ outcome }) => {
    if (!outcome) {
        return <Badge variant="outline">—</Badge>;
    }
    const badge = outcome.sent ? (
        <Badge variant="default">Sent</Badge>
    ) : outcome.error ? (
        <Badge variant="destructive">Error</Badge>
    ) : (
        <Badge variant="secondary">Skipped</Badge>
    );
    const detail = outcome.error ?? outcome.reason;
    if (!detail) {
        return badge;
    }
    return (
        <Tooltip>
            <TooltipTrigger asChild>{badge}</TooltipTrigger>
            <TooltipContent className="max-w-[320px]">{detail}</TooltipContent>
        </Tooltip>
    );
};

/** The materialized in-app inbox — exactly what the app's notification screen renders. */
const InboxCard: FC<{ userId: UserId }> = ({ userId }) => {
    const [notifications, notificationsLoadable] = useLoadableAtomValue(inAppNotificationsForUserState(userId), []);

    return (
        <Card>
            <CardHeader>
                <CardTitle>In-app inbox</CardTitle>
                <CardDescription>The read-model the app renders (users/{'{id}'}/notifications).</CardDescription>
            </CardHeader>
            <CardContent>
                {notifications.length === 0 ? (
                    <div className="section-description">
                        {notificationsLoadable.state === 'loading'
                            ? 'Loading inbox…'
                            : notificationsLoadable.state === 'hasError'
                              ? 'Couldn’t load the inbox.'
                              : 'Inbox is empty.'}
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Notification</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Created</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {notifications.map((notification) => (
                                <TableRow key={notification.id}>
                                    <TableCell className="max-w-[360px]">
                                        <div className="flex flex-col">
                                            <span className="truncate font-medium">{notification.title}</span>
                                            <span className="truncate text-xs text-muted-foreground">
                                                {notification.summary}
                                            </span>
                                        </div>
                                    </TableCell>
                                    <TableCell>{notification.type}</TableCell>
                                    <TableCell>
                                        <div className="flex gap-1">
                                            {notification.read ? (
                                                <Badge variant="secondary">Read</Badge>
                                            ) : (
                                                <Badge variant="default">Unread</Badge>
                                            )}
                                            {notification.archived && <Badge variant="outline">Archived</Badge>}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right text-muted-foreground">
                                        {formatRelative(notification.createdAt)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
};
